import { useState, useEffect, useRef } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useForm, Controller } from "react-hook-form"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { useCreateResource, useListSchemas, useValidateConnection } from "@/hooks/useResources"
import { ROUTES } from "@/routes/path"
import * as z from "zod"
import { useToast } from "@/hooks/useToast"
import { ChevronLeft, Lock, ExternalLink, AlertCircle, CheckCircle2, Loader2, Upload } from "lucide-react"
import resourceTypes from "@/lib/data/resources.json"
import { createResourceSchema, getDefaultValues, processFormData, resourceFieldVisibility } from "@/lib/utils/resource"
import { toTitleCase } from "@/lib/utils/characters"
import { ErrorCard } from "@/components/ui/error"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { useQueryClient } from "@tanstack/react-query"
import { FormField, ResourceType } from "@/types/resource"
import { MultiSelectDropdown } from "@/components/ui/multi-select-dropdown"

type ConfigureResourceFormValues = z.infer<ReturnType<typeof createResourceSchema>>

export default function ConfigureResourcePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  const queryClient = useQueryClient()

  const [selectedResource, setSelectedResource] = useState<ResourceType | null>(null)
  const [isTesting, setIsTesting] = useState(false)
  const [testResult, setTestResult] = useState<"success" | "error" | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [isProcessingFiles, setIsProcessingFiles] = useState(false)
  const [availableSchemas, setAvailableSchemas] = useState<string[]>([])
  const [isLoadingSchemas, setIsLoadingSchemas] = useState(false)
  const [hasLoadedSchemas, setHasLoadedSchemas] = useState(false)
  const isListingSchemasRef = useRef(false)

  const {
    register,
    control,
    handleSubmit,
    watch,
    getValues,
    formState: { errors, touchedFields },
    reset,
    setValue,
  } = useForm<ConfigureResourceFormValues>({
    resolver: async (data) => {
      if (!selectedResource) {
        return { values: data, errors: {} }
      }

      const schema = createResourceSchema(selectedResource, data, false)
      const result = await schema.safeParseAsync(data)

      if (result.success) {
        return { values: result.data, errors: {} }
      } else {
        const fieldErrors: Record<string, any> = {}
        result.error.errors.forEach((error) => {
          if (error.path.length > 0) {
            const fieldName = error.path[0] as string
            fieldErrors[fieldName] = {
              type: "validation",
              message: error.message,
            }
          }
        })
        return { values: {}, errors: fieldErrors }
      }
    },
    defaultValues: selectedResource ? getDefaultValues(selectedResource) : {},
  })

  const formData = watch()

  // Helper function to get error message
  const getErrorMessage = (fieldName: string): string | undefined => {
    const error = errors[fieldName as keyof typeof errors]
    if (error && typeof error === "object" && "message" in error) {
      return (error as any).message
    }
    return undefined
  }

  useEffect(() => {
    if (id) {
      const resource = resourceTypes.find((r) => r.id === id)
      if (resource) {
        setSelectedResource(resource as ResourceType)
        // Reset form with default values when resource changes
        const defaultValues = getDefaultValues(resource as ResourceType)
        reset(defaultValues)
      } else {
        navigate(ROUTES.DASHBOARD.RESOURCES.CREATE)
      }
    }
  }, [id, navigate, reset])

  const checkConnectionMutation = useValidateConnection({
    onSuccess: (success) => {
      setTestResult(success ? "success" : "error")
      toast({
        title: success ? "Connection successful" : "Connection failed",
        description: success
          ? "Your connection configuration is valid and working."
          : "Please check your configuration and try again.",
        variant: success ? "default" : "destructive",
      })
    },
    onError: (error) => {
      setTestResult("error")
      toast({
        title: "Connection test failed",
        description: error?.response?.data?.error || error?.message || "An unknown error occurred.",
        variant: "destructive",
      })
    },
    onSettled: () => {
      setIsTesting(false)
    },
  })

  const listSchemasMutation = useListSchemas({
    onSuccess: (schemas) => {
      setAvailableSchemas(schemas)
      setHasLoadedSchemas(true)
    },
    onError: (error) => {
      toast({
        title: "Failed to load schemas",
        description: error?.response?.data?.error || error?.message || "Could not fetch database schemas.",
        variant: "destructive",
      })
    },
    onSettled: () => {
      setIsLoadingSchemas(false)
    },
  })

  const handleLoadSchemas = async () => {
    if (!selectedResource || (selectedResource.id !== "postgres" && selectedResource.id !== "supabase")) return
    if (isListingSchemasRef.current || isLoadingSchemas || listSchemasMutation.isPending) return
    if (hasLoadedSchemas && availableSchemas.length > 0) return

    isListingSchemasRef.current = true
    setIsLoadingSchemas(true)
    try {
      await listSchemasMutation.mutateAsync({
        resource_type: selectedResource.id,
        meta: await processFormData(getValues(), selectedResource),
      })
    } finally {
      isListingSchemasRef.current = false
    }
  }

  const handleCheckConnection = async () => {
    if (!selectedResource) return

    setIsTesting(true)
    setTestResult(null)

    await checkConnectionMutation.mutateAsync({
      resource_type: selectedResource.id,
      meta: await processFormData(getValues(), selectedResource),
    })
  }

  const createResourceMutation = useCreateResource({
    onSuccess: () => {
      toast({
        title: "Resource created successfully",
        description: `Your ${selectedResource?.name} connection has been established.`,
      })

      queryClient.invalidateQueries({ queryKey: ["resources"] })

      navigate(ROUTES.DASHBOARD.RESOURCES.INDEX)
    },
    onError: (error: any) => {
      setFormError(error?.response?.data?.error || error?.message || "An unknown error occurred.")
    },
    onSettled: () => {
      setIsProcessingFiles(false)
    },
  })

  const onSubmit = async (data: ConfigureResourceFormValues) => {
    if (!selectedResource) return

    setFormError(null)
    setIsProcessingFiles(true)

    await createResourceMutation.mutateAsync({
      name: data.name,
      description: data.description || "",
      type: selectedResource.id,
      meta: await processFormData(data, selectedResource),
    })
  }

  const isFieldVisible = (field: FormField): boolean => {
    if (!field.visible) return true

    return resourceFieldVisibility(field.visible, formData)
  }

  const getOrderedFields = (fields: FormField[] = []): FormField[] => {
    const schemaFields = fields.filter((field) => field.name === "schema")
    const otherFields = fields.filter((field) => field.name !== "schema")
    return [...otherFields, ...schemaFields]
  }

  const renderField = (field: FormField) => {
    if (!isFieldVisible(field)) return null

    switch (field.type) {
      case "text":
        return (
          <div key={field.name} className="space-y-1 py-1">
            <Label htmlFor={field.name}>
              {field.label || toTitleCase(field.name)}
              {field.required && <span className="ml-1 text-red-500">*</span>}
            </Label>
            <Input
              {...register(field.name)}
              id={field.name}
              autoComplete="off"
              placeholder={field.label || field.name}
              error={getErrorMessage(field.name)}
              touched={touchedFields[field.name as keyof typeof touchedFields]}
            />
          </div>
        )

      case "password":
        return (
          <div key={field.name} className="space-y-1 py-1">
            <Label htmlFor={field.name} className="flex items-center gap-2">
              {field.label || toTitleCase(field.name)}
              <Lock className="h-3 w-3" />
              <span className="text-xs text-gray-500">Encrypted</span>
              {field.required && <span className="ml-1 text-red-500">*</span>}
            </Label>
            <Input
              {...register(field.name)}
              id={field.name}
              type="password"
              placeholder={field.label || field.name}
              autoComplete="off"
              error={getErrorMessage(field.name)}
              touched={touchedFields[field.name as keyof typeof touchedFields]}
            />
          </div>
        )

      case "number":
        return (
          <div key={field.name} className="space-y-1 py-2">
            <Label htmlFor={field.name}>
              {field.label || toTitleCase(field.name)}
              {field.required && <span className="ml-1 text-red-500">*</span>}
            </Label>
            <Input
              {...register(field.name)}
              id={field.name}
              type="number"
              placeholder={field.label || field.name}
              autoComplete="off"
              error={getErrorMessage(field.name)}
              touched={touchedFields[field.name as keyof typeof touchedFields]}
            />
          </div>
        )

      case "textarea":
        return (
          <div key={field.name} className="space-y-1 py-2">
            <Label htmlFor={field.name} className={field.encrypted ? "flex items-center gap-2" : ""}>
              {field.label || toTitleCase(field.name)}
              {field.encrypted && (
                <>
                  <Lock className="h-3 w-3" />
                  <span className="text-xs text-gray-500">Encrypted</span>
                </>
              )}
              {field.required && <span className="ml-1 text-red-500">*</span>}
            </Label>
            <Textarea
              {...register(field.name)}
              id={field.name}
              placeholder={field.label || field.name}
              autoComplete="off"
            />
            {getErrorMessage(field.name) && <p className="mt-1 text-sm text-red-500">{getErrorMessage(field.name)}</p>}
          </div>
        )

      case "file":
        return (
          <div key={field.name} className="space-y-1 py-2">
            <Label htmlFor={field.name} className={field.encrypted ? "flex items-center gap-2" : ""}>
              {field.label || toTitleCase(field.name)}
              {field.encrypted && (
                <>
                  <Lock className="h-3 w-3" />
                  <span className="text-xs text-gray-500">Encrypted</span>
                </>
              )}
              {field.required && <span className="ml-1 text-red-500">*</span>}
            </Label>
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  {...register(field.name)}
                  id={field.name}
                  type="file"
                  className="flex-1"
                  autoComplete="off"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                      // Validate file type for certificate files
                      const allowedTypes = [
                        "application/x-pem-file",
                        "application/x-x509-ca-cert",
                        "application/pkix-cert",
                        "text/plain",
                        "application/octet-stream",
                      ]

                      if (
                        !allowedTypes.includes(file.type) &&
                        !file.name.endsWith(".pem") &&
                        !file.name.endsWith(".crt") &&
                        !file.name.endsWith(".key")
                      ) {
                        toast({
                          title: "Invalid file type",
                          description: "Please select a valid certificate file (.pem, .crt, .key)",
                          variant: "destructive",
                        })
                        e.target.value = ""
                        return
                      }
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const fileInput = document.getElementById(field.name) as HTMLInputElement
                    fileInput?.click()
                  }}
                >
                  <Upload className="mr-1 h-4 w-4" />
                  Select
                </Button>
              </div>
            </div>
            {getErrorMessage(field.name) && <p className="mt-1 text-sm text-red-500">{getErrorMessage(field.name)}</p>}
          </div>
        )

      case "select":
        return (
          <div key={field.name} className="space-y-1 py-2">
            <Label htmlFor={field.name}>
              {field.label || toTitleCase(field.name)}
              {field.required && <span className="ml-1 text-red-500">*</span>}
            </Label>
            <Controller
              name={field.name}
              control={control}
              render={({ field: controllerField }) => (
                <Select value={controllerField.value || field.default || ""} onValueChange={controllerField.onChange}>
                  <SelectTrigger className={getErrorMessage(field.name) ? "border-red-500" : ""}>
                    <SelectValue placeholder={`Select ${field.label || field.name}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {field.options?.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {getErrorMessage(field.name) && <p className="mt-1 text-sm text-red-500">{getErrorMessage(field.name)}</p>}
          </div>
        )

      case "multi-select": {
        if (field.name !== "schema") {
          return null
        }

        const selectedSchemas = Array.isArray((formData as Record<string, any>)["schema"])
          ? ((formData as Record<string, any>)["schema"] as string[]) || []
          : []

        return (
          <div key={field.name} className="space-y-2 py-2">
            <Label htmlFor={field.name}>{field.label || toTitleCase(field.name)}</Label>

            <MultiSelectDropdown
              options={availableSchemas}
              value={selectedSchemas}
              onChange={(next) => {
                setValue("schema", next as any, {
                  shouldDirty: true,
                  shouldValidate: true,
                  shouldTouch: true,
                })
              }}
              placeholder={isLoadingSchemas ? "Loading schemas..." : "Select schemas"}
              allLabel="All schemas"
              emptyContent={
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Fetch available schemas from your database to select one, multiple, or all schemas for analysis.
                  </p>
                  <button
                    type="button"
                    className="text-sm text-primary underline underline-offset-4"
                    onClick={handleLoadSchemas}
                    disabled={isLoadingSchemas}
                  >
                    {isLoadingSchemas ? "Loading schemas..." : "Load schemas"}
                  </button>
                </div>
              }
            />
            {getErrorMessage(field.name) && <p className="mt-1 text-sm text-red-500">{getErrorMessage(field.name)}</p>}
          </div>
        )
      }

      case "tab":
        return (
          <div key={field.name} className="space-y-1 py-2">
            <Label className="font-medium text-gray-900 dark:text-white">
              {field.label || toTitleCase(field.name)}
            </Label>
            <Controller
              name={field.name}
              control={control}
              render={({ field: controllerField }) => (
                <div className="mt-2 flex gap-2">
                  {field.options?.map((option) => (
                    <Button
                      key={option.value}
                      type="button"
                      variant={controllerField.value === option.value ? "default" : "outline"}
                      onClick={() => controllerField.onChange(option.value)}
                      size="xs"
                      className="h-7 py-2 text-xs"
                    >
                      {option.name}
                    </Button>
                  ))}
                </div>
              )}
            />
            {getErrorMessage(field.name) && <p className="mt-1 text-sm text-red-500">{getErrorMessage(field.name)}</p>}
          </div>
        )

      case "flex-row":
        return (
          <div key={field.name}>
            {field.label && (
              <Label className="mb-2 block font-medium text-gray-900 dark:text-white">{field.label}</Label>
            )}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
              {field.fields?.map(renderField)}
            </div>
          </div>
        )

      case "group":
        return (
          <div key={field.name} className="space-y-1">
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
              {field.label || toTitleCase(field.name)}
            </h3>
            {field.fields?.map(renderField)}
          </div>
        )

      default:
        return null
    }
  }

  if (!selectedResource) {
    return (
      <div className="mx-auto max-w-6xl p-6">
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-2">
      {/* Header with back button and close button */}
      <div className="mb-4">
        <div className="mb-3 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)} className="flex items-center gap-1">
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
        </div>
        <div className="mb-1 flex items-center gap-3">
          <img src={selectedResource.icon} alt={selectedResource.name} className="h-5 w-5" />
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">{selectedResource.name}</h1>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">Configure your {selectedResource.name} connection</p>
      </div>

      <div className="max-w-6xl">
        {/* Main form */}
        <div className="space-y-5">
          {/* Information banner */}
          <Alert variant="info">
            <AlertCircle className="h-5 w-5 stroke-blue-800" />
            <AlertDescription className="text-sm">
              <span>
                {" "}
                Whitelist the IP <strong>178.156.173.145</strong> on your database instance to connect to it.
              </span>
              <a
                href="https://docs.ledgerbeam.com/how-to-guides/whitelist-ip-addresses"
                target="_blank"
                className="ml-2 inline-flex items-center gap-1 text-sm hover:underline"
                rel="noreferrer"
              >
                Learn more
                <ExternalLink className="h-3 w-3" />
              </a>
            </AlertDescription>
          </Alert>

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Basic information */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Input
                    label="Name"
                    {...register("name")}
                    error={getErrorMessage("name")}
                    touched={touchedFields.name}
                    placeholder={`My ${selectedResource.name}`}
                  />
                </div>
                <div>
                  <Input
                    label="Description (optional)"
                    {...register("description")}
                    error={getErrorMessage("description")}
                    touched={touchedFields.description}
                    placeholder="Production database for analytics"
                  />
                </div>
              </div>
            </div>

            {/* Dynamic fields based on resource configuration */}
            {getOrderedFields(selectedResource.fields).map(renderField)}

            {formError && <ErrorCard message={formError} />}

            {/* Test result */}
            {testResult && (
              <Alert variant={testResult === "success" ? "success" : "error"}>
                {testResult === "success" ? <CheckCircle2 className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
                <AlertDescription>
                  <span className="font-medium">
                    {testResult === "success"
                      ? "Connection successful!"
                      : "Connection failed. Please check your configuration."}
                  </span>
                </AlertDescription>
              </Alert>
            )}

            {/* Action buttons */}
            <div className="mb-6 flex justify-between pt-10">
              <Button type="button" variant="outline" onClick={handleCheckConnection} disabled={isTesting}>
                {isTesting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Testing...
                  </>
                ) : (
                  "Test configuration"
                )}
              </Button>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => navigate(ROUTES.DASHBOARD.RESOURCES.INDEX)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createResourceMutation.isPending || isProcessingFiles}>
                  {createResourceMutation.isPending || isProcessingFiles ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {isProcessingFiles ? "Processing files..." : "Saving..."}
                    </>
                  ) : (
                    "Save"
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
